import { FC, forwardRef } from 'react';
import { Root, Row, ContentSide, ImageSide, Images, ImageItem } from './styles';
import SlideInOnScroll from '@components/Transitions/SlideInOnScroll';

const RADIUS = '6rem';
const MOBILE_RADIUS = '3rem';
const ZERO = '0';

interface ImageRadius {
  borderTopLeftRadius?: string;
  borderTopRightRadius?: string;
  borderBottomLeftRadius?: string;
  borderBottomRightRadius?: string;
}

function getImageRadius({
  index,
  total,
  orientation = 'left',
  isGrid2x2 = false,
  isMobile = false,
  isLastItem = false,
}: {
  index: number;
  total: number;
  orientation: 'left' | 'right';
  isGrid2x2: boolean;
  isMobile?: boolean;
  isLastItem?: boolean;
}): ImageRadius {
  const radius: ImageRadius = {
    borderTopLeftRadius: ZERO,
    borderTopRightRadius: ZERO,
    borderBottomLeftRadius: ZERO,
    borderBottomRightRadius: ZERO,
  };

  if (isMobile) {
    radius.borderTopRightRadius = MOBILE_RADIUS;
    radius.borderBottomRightRadius = MOBILE_RADIUS;
    return radius;
  }

  // 🔹 1 IMAGE
  if (total === 1) {
    if (orientation === 'left') {
      radius.borderTopRightRadius = RADIUS;
      radius.borderBottomRightRadius = RADIUS;
    } else {
      radius.borderTopLeftRadius = RADIUS;
      radius.borderBottomLeftRadius = RADIUS;
    }

    if (isLastItem) {
      if (orientation === 'left') {
        radius.borderBottomRightRadius = ZERO;
      } else {
        radius.borderBottomLeftRadius = ZERO;
      }
    }

    return radius;
  }

  // 🔹 GRID 2x2 (indexes: 0 1 / 2 3)
  if (isGrid2x2) {
    if (orientation === 'left') {
      if (index === 1) radius.borderTopRightRadius = RADIUS;
      if (index === 3) radius.borderBottomRightRadius = RADIUS;
    } else {
      if (index === 0) radius.borderTopLeftRadius = RADIUS;
      if (index === 2) radius.borderBottomLeftRadius = RADIUS;
    }
    return radius;
  }

  // 🔹 COLUMN (2+ images)
  const isTop = index === 0;
  const isBottom = index === total - 1;

  if (orientation === 'left') {
    if (isTop) radius.borderTopRightRadius = RADIUS;
    if (isBottom) radius.borderBottomRightRadius = RADIUS;
  } else {
    if (isTop) radius.borderTopLeftRadius = RADIUS;
    if (isBottom) radius.borderBottomLeftRadius = RADIUS;
  }

  return radius;
}

interface IllustratedTextProps {
  images: Array<string | ((height: string) => JSX.Element)>;
  content: JSX.Element;
  orientation?: 'left' | 'right';
  isGrid2x2?: boolean;
  isLastItem?: boolean;
  verticalContentPadding?: string;
}

const IllustratedText = forwardRef<HTMLDivElement, IllustratedTextProps>(
  (
    {
      images,
      content,
      orientation = 'left',
      isGrid2x2 = false,
      isLastItem = false,
      verticalContentPadding,
    },
    ref
  ) => {
    return (
      <Root ref={ref} isLastItem={isLastItem}>
        <Row>
          <div className="mobile-first-image">
            <ImageItem
              {...getImageRadius({
                index: 0,
                total: images.length,
                orientation,
                isGrid2x2,
                isMobile: true,
                isLastItem,
              })}
            >
              {typeof images[0] === 'string' ? (
                <img src={images[0]} alt="Illustrated Text Image" />
              ) : (
                images?.[0]('100%')
              )}
            </ImageItem>
          </div>

          <ContentSide
            $orientation={orientation}
            verticalContentPadding={verticalContentPadding}
          >
            <SlideInOnScroll
              direction={orientation === 'left' ? 'right' : 'left'}
            >
              {content}
            </SlideInOnScroll>
          </ContentSide>

          <ImageSide $orientation={orientation} $isLastItem={isLastItem}>
            <SlideInOnScroll
              direction={orientation === 'left' ? 'left' : 'right'}
            >
              <Images $isGrid2x2={isGrid2x2}>
                {images.map((src, index) => {
                  const radius = getImageRadius({
                    index,
                    total: images.length,
                    orientation,
                    isGrid2x2,
                    isMobile: false,
                    isLastItem,
                  });

                  return (
                    <ImageItem
                      key={typeof src === 'string' ? src : src.length}
                      {...radius}
                      className={
                        index > 0 ? 'hide-on-mobile' : 'hide-on-mobile'
                      }
                    >
                      {typeof src === 'string' ? (
                        <img src={src} alt="Illustrated Text Image" />
                      ) : (
                        src('100%')
                      )}
                    </ImageItem>
                  );
                })}
              </Images>
            </SlideInOnScroll>
          </ImageSide>
        </Row>
      </Root>
    );
  }
);

IllustratedText.displayName = 'IllustratedText';

export default IllustratedText;
